/**
 * Pure date/time helpers used by DateTimeSelector and BetweenDateTimeSelector.
 *
 * Extracted into a separate module so they can be tested without pulling in
 * component-level imports (React, MUI icons, TimeDisplayMode, etc.).
 */

// ── Timezone-aware getters ───────────────────────────────────────────

export function getYear(d: Date, utc: boolean): number {
  return utc ? d.getUTCFullYear() : d.getFullYear();
}

export function getMonth(d: Date, utc: boolean): number {
  return utc ? d.getUTCMonth() : d.getMonth();
}

export function getDateOfMonth(d: Date, utc: boolean): number {
  return utc ? d.getUTCDate() : d.getDate();
}

export function getHours(d: Date, utc: boolean): number {
  return utc ? d.getUTCHours() : d.getHours();
}

export function getMinutes(d: Date, utc: boolean): number {
  return utc ? d.getUTCMinutes() : d.getMinutes();
}

export function getSeconds(d: Date, utc: boolean): number {
  return utc ? d.getUTCSeconds() : d.getSeconds();
}

// ── Date construction ────────────────────────────────────────────────

export function buildDate(
  year: number,
  month: number,
  day: number,
  hours: number,
  minutes: number,
  seconds: number,
  utc: boolean,
): Date {
  if (utc) {
    return new Date(Date.UTC(year, month, day, hours, minutes, seconds));
  }
  return new Date(year, month, day, hours, minutes, seconds);
}

/** Days in a given month (0-indexed). */
export function daysInMonth(year: number, month: number, utc: boolean): number {
  // Day 0 of the *next* month gives the last day of this month.
  if (utc) {
    return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  }
  return new Date(year, month + 1, 0).getDate();
}

/** Day of week for the first of the month (0 = Sunday). */
export function firstDayOfWeek(
  year: number,
  month: number,
  utc: boolean,
): number {
  const d = utc ? new Date(Date.UTC(year, month, 1)) : new Date(year, month, 1);
  return utc ? d.getUTCDay() : d.getDay();
}

/** Converts a 12-hour value + AM/PM flag to 24-hour format. */
export function to24h(hour12: number, isPm: boolean): number {
  if (isPm) {
    return hour12 === 12 ? 12 : hour12 + 12;
  }
  return hour12 === 12 ? 0 : hour12;
}
